import React, { useCallback, useMemo } from 'react';

import { SidePage } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { useQueryClient } from '@gogame/query';
import { useLinkBasenameWithRoot, useScreenState } from '@gogame/screens';

import ConceptFamilyDetails from 'scenes/Gallery/GalleryPage/ConceptFamilies/ConceptFamilyDetails';
import ConceptDetailsSidePage from 'scenes/Gallery/GalleryPage/Concepts/ConceptDetails';
import {
  CreativeComments,
  CreativeCommentsProvider,
} from 'scenes/Gallery/GalleryPage/Creatives/Comments';
import { useCommentsCount } from 'scenes/Gallery/GalleryPage/Creatives/Comments/data';
import {
  useCreativeDetails,
  useIsUpdating,
} from 'scenes/Gallery/GalleryPage/Creatives/data';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';
import {
  CONCEPT_ID,
  TAB_DETAILS,
  TAB_COMMENTS,
  TAB_PERFORMANCE,
  TAB_AI_TAGS,
  TAB_ACTIONS_LOG,
} from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { conceptPreviewLink } from 'scenes/Gallery/GalleryPage/navigation';
import { galleryLink } from 'scenes/Gallery/GalleryPage/navigation';
import BusinessPartnerDetails from 'scenes/BusinessPartners/components/BusinessPartnerDetails';

import CreativeDetails from './CreativeDetails';
import ActionsLogTab from './components/ActionLogsTab';
import PerformanceTab from './components/PerformanceTab';
import AITagsTab from './components/AITagsTab';

const CreativeDetailsSidePage = ({
  id,
  activeTabId = TAB_DETAILS,
  onClose,
  onExited,
  open,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();
  const { isAllowPerformance } = useGalleryPermissions();
  const galleryBasename = useLinkBasenameWithRoot(galleryLink);
  const { data, isLoading, isRefetching } = useCreativeDetails({ id });
  const commentCount = useCommentsCount(id);
  const isUpdating = useIsUpdating();
  const queryClient = useQueryClient();
  const screenState = useScreenState();

  const title = useMemo(() => {
    return data?.[CREATIVE.IS_ORIGINAL]
      ? 'Creative Original'
      : 'Creative Variant';
  }, [data?.[CREATIVE.IS_ORIGINAL]]);

  const subTitle = useMemo(() => {
    return data
      ? `${data?.[CREATIVE.NAME]} / ${data?.[CREATIVE.SHORT_NAME]}`
      : '';
  }, [data?.[CREATIVE.NAME], data?.[CREATIVE.SHORT_NAME]]);

  const entityId = useMemo(() => {
    return `${data?.[CREATIVE.ID] || ''} / key ${data?.[CREATIVE.KEY] || ''}`;
  }, [data?.[CREATIVE.ID], data?.[CREATIVE.KEY]]);

  const aiTagsCount = useMemo(() => {
    return data?.[CREATIVE.AI_TAGS]?.length || '0';
  }, [data?.[CREATIVE.AI_TAGS]]);

  const handleOpenConcept = (conceptId) => {
    openChildComponent(ConceptDetailsSidePage, {
      id: conceptId,
    });
  };

  const handleRemove = () => {
    onClose();
  };

  const handleOpenInNewTab = useCallback(() => {
    const link = conceptPreviewLink.stringify(
      {
        [CONCEPT_ID]: `${data?.[CREATIVE.CONCEPT].id}`,
        filters: screenState.filters,
        creatives: {
          tableSettings: {
            orderDir: 'asc',
          },
        },
      },
      {
        basename: galleryBasename,
      }
    );
    window.open(link);
  }, [data?.id]);

  const handleOpenBusinessPartner = useCallback(
    (id) => {
      openChildComponent(BusinessPartnerDetails, {
        id,
        onChange: () => {
          queryClient.resetQueries({ active: true });
        },
      });
    },
    [openChildComponent, BusinessPartnerDetails, queryClient]
  );

  const handleOpenConceptFamily = useCallback(
    (id) => {
      openChildComponent(ConceptFamilyDetails, {
        id,
        onChange: () => {
          queryClient.resetQueries({ active: true });
        },
      });
    },
    [openChildComponent, ConceptFamilyDetails, queryClient]
  );

  return (
    <SidePage
      onClose={onClose}
      backdrop={false}
      onExited={onExited}
      loading={isLoading || isUpdating}
      fetching={isRefetching}
      open={open}
      size={'lg'}
    >
      <SidePage.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePage.Tabs activeTabId={activeTabId}>
        <SidePage.Tab label={TAB_DETAILS} data-cy-label={'Details'}>
          {!!data && (
            <CreativeDetails
              data={data}
              onOpenConcept={handleOpenConcept}
              onOpenInNewTab={handleOpenInNewTab}
              onOpenBusinessPartner={handleOpenBusinessPartner}
              onOpenConceptFamily={handleOpenConceptFamily}
              onRemove={handleRemove}
              openChildComponent={openChildComponent}
            />
          )}
        </SidePage.Tab>
        <SidePage.Tab
          label={TAB_AI_TAGS}
          data-cy-label={'AI Tags'}
          count={aiTagsCount}
        >
          <AITagsTab data={data?.[CREATIVE.AI_TAGS]} />
        </SidePage.Tab>
        <SidePage.Tab
          label={TAB_COMMENTS}
          data-cy-label={'Comments'}
          count={commentCount || '0'}
        >
          <SidePage.Content disableGutters={true}>
            <CreativeComments itemSize={12} />
          </SidePage.Content>
        </SidePage.Tab>
        {isAllowPerformance && (
          <SidePage.Tab label={TAB_PERFORMANCE} data-cy-label={'Performance'}>
            <PerformanceTab creativeId={id} />
          </SidePage.Tab>
        )}
        <SidePage.Tab label={TAB_ACTIONS_LOG} data-cy-label={'Usage Logs'}>
          <ActionsLogTab creativeId={id} />
        </SidePage.Tab>
      </SidePage.Tabs>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
      {childComponent}
    </SidePage>
  );
};
export default (props) => {
  return (
    <CreativeCommentsProvider creativeId={props.id}>
      <CreativeDetailsSidePage {...props} />
    </CreativeCommentsProvider>
  );
};
